import { Capacitor } from '@capacitor/core';
import {
  ToastOptions,
  AlertOptions,
  alertController,
  toastController,
} from '@ionic/core';
import { addDays, addMinutes, addHours } from 'date-fns';
import { TypeKeys } from './index';
import { translate } from '../global/translate';
import { amplitudeLogEvent, AmplitudeEvents } from '../global/amplitude';
import {
  LocalNotificationPendingList,
  LocalNotifications,
  ScheduleResult,
} from '@capacitor/local-notifications';

export interface SetNotificationsPermission {
  type: TypeKeys.SET_NOTIFICATIONS_PERMISSION;
  notificationsPermission: SworkitState['notifications']['notificationsPermission'];
}

const setNotificationsPermission = (
  notificationsPermission: SworkitState['notifications']['notificationsPermission']
) => async (dispatch, _getState) => {
  const action: SetNotificationsPermission = {
    type: TypeKeys.SET_NOTIFICATIONS_PERMISSION,
    notificationsPermission,
  };
  return dispatch(action);
};

export const checkNotificationsPermission = () => async (
  dispatch,
  _getState
) => {
  dispatch(setNotificationsEnabled(Capacitor.isNativePlatform()));
  try {
    const permission = await LocalNotifications.checkPermissions();
    console.debug('permission', permission);
    dispatch(setNotificationsPermission(permission.display));
  } catch (err) {
    console.info(err);
    return dispatch(setNotificationsEnabled(false));
  }
};

export const getPendingNotifications = () => async (dispatch, _getState) => {
  try {
    const pendingNotifications = await LocalNotifications.getPending();
    dispatch(setNotificationsEnabled(true));
    return dispatch(setPendingNotifications(pendingNotifications));
  } catch (err) {
    console.info(err);
    return dispatch(setNotificationsEnabled(false));
  }
};

export const askNotificationPermission = () => async (dispatch, getState) => {
  if(!Capacitor.isNativePlatform()) return;
  const {
    notifications: { notificationsPermission },
  } = getState() as SworkitState;
  console.debug('ask notification permission', notificationsPermission);
  if (notificationsPermission === 'prompt') {
    const res = await LocalNotifications.requestPermissions();
    console.debug('notification permission res', res);
    if (res.display !== 'granted') {
      await showNoPermissionAlert();
    }
    dispatch(checkNotificationsPermission());
  } else if (notificationsPermission === 'denied') {
    return showNoPermissionAlert();
  }
};

export const scheduleDailyReminder = (timeString: string) => async (
  dispatch,
  getState
) => {
  await dispatch(askNotificationPermission());
  if (!hasNotificationPermission(getState())) return;
  const date = new Date(timeString);
  const hr = date.getHours();
  const min = date.getMinutes();
  const time = getTimeString(hr, min);
  await scheduleDailyLocalNotification(hr, min);
  dispatch(setDailyReminderTime(timeString));
  showReminderSetToast(time);
  dispatch(getPendingNotifications());
  amplitudeLogEvent(AmplitudeEvents.scheduled_daily_reminder, {
    date,
    hr,
    min,
    time,
  });
};

export const scheduleInactivityReminder = (
  timeString: string,
  inactiveDays: number,
  showToast?: boolean
) => async (dispatch, getState) => {
  await dispatch(askNotificationPermission());
  if (!hasNotificationPermission(getState())) return;
  dispatch(setInactivityReminderTime(timeString));
  const date = new Date(timeString);
  const hr = date.getHours();
  const min = date.getMinutes();

  const time = getTimeString(hr, min);
  await scheduleInactivityLocalNotification(hr, min, inactiveDays);
  if (showToast) {
    showReminderSetToast(time);
    amplitudeLogEvent(AmplitudeEvents.scheduled_inactivity_reminder, {
      date,
      hr,
      min,
      time,
    });
  }
  dispatch(getPendingNotifications());
};

export interface CancelNotificationById {
  type: TypeKeys.CANCEL_NOTIFICATION_BY_ID;
  id: number;
}
export const cancelNotificationById = (id: number) => async (
  dispatch,
  _getState
) => {
  const action: CancelNotificationById = {
    type: TypeKeys.CANCEL_NOTIFICATION_BY_ID,
    id,
  };
  dispatch(action);
  const pending: ScheduleResult = {
    notifications: [{ id }],
  };
  await LocalNotifications.cancel(pending);
  dispatch(getPendingNotifications());
};

export interface SetInactiveDays {
  type: TypeKeys.SET_INACTIVE_DAYS;
  days: number;
}
export const setInactiveDays = (days: number) => async (
  dispatch,
  _getState
) => {
  const action: SetInactiveDays = {
    type: TypeKeys.SET_INACTIVE_DAYS,
    days,
  };
  return dispatch(action);
};

export const resetInactivityReminder = () => async (dispatch, _getState) => {
  try {
    const pendingNotifications = await LocalNotifications.getPending();
    if (!pendingNotifications) return;
    const inactivityReminder = pendingNotifications.notifications.find(
      (notification) => notification.id == 2
    );
    if (!inactivityReminder) return;
    const currentState: SworkitState = _getState();
    const time = currentState.notifications.inactiveReminderTime;
    const inactiveDays = currentState.notifications.inactiveDays;
    if (time && inactiveDays) {
      dispatch(scheduleInactivityReminder(time, inactiveDays));
    }
  } catch (err) {
    console.info(err);
    return dispatch(setNotificationsEnabled(false));
  }
};

export interface SetInactivityReminderTime {
  type: TypeKeys.SET_INACTIVE_REMINDER_TIME;
  inactiveReminderTime: string;
}

export const setInactivityReminderTime = (
  inactiveReminderTime: string
) => async (dispatch, _getState) => {
  const action: SetInactivityReminderTime = {
    type: TypeKeys.SET_INACTIVE_REMINDER_TIME,
    inactiveReminderTime,
  };
  return dispatch(action);
};

export interface SetDailyReminderTime {
  type: TypeKeys.SET_DAILY_REMINDER_TIME;
  dailyReminderTime: string;
}

export const setDailyReminderTime = (dailyReminderTime: string) => async (
  dispatch,
  _getState
) => {
  const action: SetDailyReminderTime = {
    type: TypeKeys.SET_DAILY_REMINDER_TIME,
    dailyReminderTime,
  };
  return dispatch(action);
};

export interface SetPendingNotifications {
  type: TypeKeys.SET_PENDING_NOTIFICATIONS;
  pendingNotifications: LocalNotificationPendingList;
}

const setPendingNotifications = (
  pendingNotifications: LocalNotificationPendingList
) => async (dispatch, _getState) => {
  const action: SetPendingNotifications = {
    type: TypeKeys.SET_PENDING_NOTIFICATIONS,
    pendingNotifications,
  };
  return dispatch(action);
};

export interface SetNotificationsEnabled {
  type: TypeKeys.SET_NOTIFICATIONS_ENABLED;
  notificationsEnabled: boolean;
}
const setNotificationsEnabled = (notificationsEnabled: boolean) => (
  dispatch,
  _getState
) => {
  const action: SetNotificationsEnabled = {
    type: TypeKeys.SET_NOTIFICATIONS_ENABLED,
    notificationsEnabled,
  };
  return dispatch(action);
};

export interface SetNextWorkoutReminderDate {
  type: TypeKeys.SET_NEXT_WORKOUT_REMINDER_DATE;
  nextWorkoutReminderDate: string;
}

function setNextWorkoutReminderDate(nextWorkoutReminderDate: string) {
  const action: SetNextWorkoutReminderDate = {
    type: TypeKeys.SET_NEXT_WORKOUT_REMINDER_DATE,
    nextWorkoutReminderDate,
  };
  return action;
}

export const scheduleNextWorkout = (timeString: string) => async (
  dispatch,
  getState
) => {
  if (!timeString) return;
  await dispatch(askNotificationPermission());
  if (!hasNotificationPermission(getState())) return;
  dispatch(setNextWorkoutReminderDate(timeString));
  const date = new Date(timeString);
  const now = new Date();
  console.debug('schedule workout', timeString, date, now);
  if (date <= now) {
    showFutureAlert();
  } else {
    const hr = date.getHours();
    const min = date.getMinutes();
    const time = getTimeString(hr, min);
    await scheduleOnceLocalNotification(date);
    showReminderSetToast(time);
    amplitudeLogEvent(AmplitudeEvents.scheduled_next_workout, {
      date,
      hr,
      min,
      time,
    });
  }
  dispatch(getPendingNotifications());
};

async function scheduleInactivityLocalNotification(
  hour: number,
  minute: number,
  inactiveDays: number
) {
  if (typeof hour == 'undefined' || typeof minute == 'undefined') {
    console.debug("don't schedule inactivity reminder: ", hour, minute);
    return;
  }
  const today = new Date();
  const midnight = today.setHours(0, 0, 0, 0);
  const alertDate = addDays(midnight, inactiveDays);
  const alertTime = addMinutes(addHours(alertDate, hour), minute);
  console.debug('schedule inactivity reminder', alertTime);
  const pendingNotifs = await LocalNotifications.schedule({
    notifications: [
      {
        title: translate('WORKOUT_REMINDER'),
        body: translate('TOO_LONG'),
        id: 2,
        schedule: {
          at: alertTime,
        },
      },
    ],
  });
  return pendingNotifs;
}

async function scheduleDailyLocalNotification(hour: number, minute: number) {
  if (typeof hour == 'undefined' || typeof minute == 'undefined') {
    console.debug("don't schedule daily reminder: ", hour, minute);
    return;
  }
  console.debug('schedule daily reminder', hour, minute);
  const pendingNotifs = await LocalNotifications.schedule({
    notifications: [
      {
        title: translate('WORKOUT_REMINDER'),
        body: translate('TIME_TO_SWORKIT'),
        id: 1,
        schedule: {
          every: 'day',
          on: {
            hour,
            minute,
          },
        },
      },
    ],
  });
  return pendingNotifs;
}

async function scheduleOnceLocalNotification(date: Date) {
  if (!date) {
    console.debug("don't schedule once: ", date);
    return;
  }
  console.debug('schedule once: ', date);
  const pendingNotifs = await LocalNotifications.schedule({
    notifications: [
      {
        title: translate('WORKOUT_REMINDER'),
        body: translate('TIME_TO_SWORKIT'),
        id: 3,
        // sound: 'beep.aiff',
        // attachments: [{ id: 'face', url: 'res://public/assets/ionitron.png' }],
        schedule: {
          at: date,
        },
        // actionTypeId: 'OPEN_PRODUCT',
        // extra: {
        //   productId: 'PRODUCT-1'
        // }
      },
    ],
  });
  return pendingNotifs;
}

function getTimeString(hr: number, min: number) {
  const ampm = hr >= 12 ? translate('PM') : translate('AM');
  const hour = hr > 12 ? hr - 12 : hr === 0 ? 12 : hr;
  const minutes = min < 10 ? `0${min}` : min;
  const time = `${hour}:${minutes} ${ampm}`;
  return time;
}

async function showNoPermissionAlert() {
  const alertOptions: AlertOptions = {
    header: translate('UPDATE_REMINDER'),
    buttons: [
      {
        text: translate('OK'),
      },
    ],
  };
  const alert = await alertController.create(alertOptions);
  return alert.present();
}

async function showReminderSetToast(time: string) {
  const options: ToastOptions = {
    message: translate('REMINDER_ENABLED_FOR', { time }),
    position: 'bottom',
    duration: 3000,
    color: 'dark',
  };
  const toast = await toastController.create(options);
  return await toast.present();
}

async function showFutureAlert() {
  const alertOptions: AlertOptions = {
    header: translate('MUST_BE_IN_FUTURE'),
    buttons: [
      {
        text: translate('OK'),
      },
    ],
  };
  const alert = await alertController.create(alertOptions);
  return alert.present();
}

export function hasNotificationPermission(state: SworkitState) {
  return state.notifications.notificationsPermission === 'granted';
}
